import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import Panel from 'common/panel';
import Carousel from 'common/carousel/carousel';
import { Div, Flex, Button, NoteTitle, NoteName, NoteText, Icon } from 'ui';
import styled from 'styled-components';
import { EditOutlined } from '@ant-design/icons';
import EditNotesSidepanel from './edit-notes-sidepanel';

const ButtonWrapper = styled(Div)`
  flex-shrink: 0;
  margin-right: 1px;
  ${p => !p.visible && 'display: none'};
  button {
    box-shadow: none !important;
  }
`;

const LockedIcon = styled(Icon)`
  color: ${p => p.theme.colors.alerts[2]};
`;

const LockedText = styled.span`
  color: ${p => p.theme.colors.alerts[2]};
  font-weight: ${p => p.theme.fontWeight.bolder};
`;

const Notes = ({
  notes,
  intl,
  type,
  save,
  loading,
  small = false,
  large = false,
  error,
  readonly = false,
}) => {
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  const getInitialNoteIndex = (notesArray, noteType) => {
    if (notesArray && notesArray.length && noteType) {
      let idx = -1;
      if (noteType === 'notification') {
        idx = notesArray.findIndex(n => n.noteType.toUpperCase() === 'EQM');
      } else {
        idx = notesArray.findIndex(n =>
          n.ownerObjectType?.toLowerCase() === noteType?.toLowerCase(),
        );
      }
      if (idx > -1) {
        return idx;
      }
    }
    return 0;
  };

  const openEditNotesSidepanel = () => { setIsSidePanelOpen(true); };

  const closeEditNotesSidepanel = () => { setIsSidePanelOpen(false); };

  const saveNotes = (payload) => {
    save(payload);
  };

  return (
    <>
      <Panel
        title={
          <span>
            {intl.formatMessage({ id: 'notes.TITLE' })}({notes?.filter(n => !n.isEmptyNote).length}
            )
          </span>
        }
      >
        {notes && notes.length ?
          <Carousel
            initialSlide={getInitialNoteIndex(notes, type)}
            itemsToHighlight={notes?.map((v, i) =>
              ({ index: i, isEmptyNote: v.isEmptyNote }),
            )?.filter(n => !n.isEmptyNote)}
          >
            {notes?.map(note =>
              <Div key={note?.id} data-test-id={`notes.${note?.noteType?.toUpperCase()}`}>
                <Flex marginBottom={5}>
                  <NoteTitle>
                    <NoteName data-test-id='noteTitle'>
                      {intl.formatMessage({
                        id: note?.noteType ?
                          `notes.${note.noteType.toUpperCase()}` :
                          'notes.NULL',
                      })}
                    </NoteName>
                  </NoteTitle>
                  {note?.isEntityLocked && !readonly &&
                    <LockedText>
                      <LockedIcon type='exclamation-triangle' />{' '}
                      <FormattedMessage id='order.status.locked' />
                    </LockedText>
                  }
                  <ButtonWrapper
                    visible={note.editable && !note?.isEntityLocked && !readonly}
                    onClick={openEditNotesSidepanel}
                    data-test-id='noteEditBtn'
                  >
                    <Button type='secondary' icon={<EditOutlined />} />
                  </ButtonWrapper>
                </Flex>
                <NoteText large={large} small={small}>
                  <span>
                    {note &&
                      note.noteTexts &&
                      note.noteTexts.reduce(
                        (acc, curr) => `${acc}${acc ? '\n' : ''}${curr.textLine || ''}`,
                        '',
                      )}
                  </span>
                </NoteText>
              </Div>,
            )}
          </Carousel>
          :
          <FormattedMessage id='notes.NO_ORDER' />
        }
      </Panel>
      {isSidePanelOpen &&
        <EditNotesSidepanel
          notes={notes}
          close={closeEditNotesSidepanel}
          save={saveNotes}
          isLoading={loading}
          error={error}
        />
      }
    </>
  );
};

Notes.propTypes = {
  notes: PropTypes.array,
  intl: PropTypes.object,
  type: PropTypes.string,
  save: PropTypes.func,
  loading: PropTypes.bool,
  small: PropTypes.bool,
  large: PropTypes.bool,
  error: PropTypes.object,
  readonly: PropTypes.bool,
};

export default injectIntl(Notes);
